import { Dispatch, FC, SetStateAction, useState } from 'react';
import {
    Button,
    ButtonStyles,
    Dropdown,
    IconButton,
    IconButtonStyles,
    Icons,
    List,
    ListItem,
    Search
} from '@silverfort/sf-components';
import { ENTITY_TYPE_TO_ICON, ENTITY_TYPES, FIELD_OPTIONS } from './consts';
import { Entity } from '../../../api';
import { useLazySearchEntities } from '../../../api';

interface Props {
    onClose: () => void;
    setSelectedEntities: Dispatch<SetStateAction<Entity[]>>;
    selectedEntities: Entity[];
    setIsDirty?: Dispatch<SetStateAction<boolean>>;
    selectedEntityTypes?: number[];
    allowSelectTypes?: boolean;
}

const EntitiesSearch: FC<Props> = ({
    onClose,
    setSelectedEntities,
    selectedEntities,
    setIsDirty,
    selectedEntityTypes,
    allowSelectTypes = true
}) => {
    const [entityTypes, setEntityTypes] = useState<number[]>(selectedEntityTypes ?? []);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [displayedField, setDisplayedField] = useState<string>(FIELD_OPTIONS[0].value!);
    const { data, isLoading, searchEntities } = useLazySearchEntities();

    const onSearch = (newSearchedQuery: string) => {
        setSearchQuery(newSearchedQuery);
        searchEntities({ searchQuery: newSearchedQuery, entitiesTypes: entityTypes });
    };

    const onEntityTypeChange = (types?: number | number[]) => {
        setEntityTypes((types ?? []) as number[]);
    };

    const onDisplayFieldChange = (newDisplayField?: string | string[]) => {
        setDisplayedField(newDisplayField as string);
    };

    const entitySelected = (toggledEntity?: Entity) => {
        if (!toggledEntity) return;

        setSelectedEntities((prevState) => {
            if (prevState.some((entity) => entity.guid === toggledEntity.guid)) {
                return prevState.filter((previousSelected) => previousSelected.guid !== toggledEntity.guid);
            }
            return [...prevState, toggledEntity];
        });

        setIsDirty?.(true);
    };

    return (
        <div className="h-full w-full flex flex-col rounded-lg border shadow-md">
            <div className="h-14 w-full pl-6 pr-4 border-b flex justify-between items-center">
                <div className="text-14 font-sf font-medium">Select scope</div>
                <IconButton icon={Icons.Cancel} buttonStyle={IconButtonStyles.Secondary} onClick={onClose} />
            </div>
            <div className="h-14 w-full px-4 border-b flex justify-end items-center gap-2">
                <Dropdown
                    variant="filter"
                    selectedItems={entityTypes}
                    text="Type"
                    items={ENTITY_TYPES}
                    onChange={onEntityTypeChange}
                    multiple
                    disabled={!allowSelectTypes}
                />
                <Dropdown
                    variant="trigger"
                    selectedItems={displayedField}
                    items={FIELD_OPTIONS}
                    onChange={onDisplayFieldChange}
                    size="small"
                    className="!w-[128px]"
                />
            </div>
            <div className="w-full flex-grow flex flex-col">
                <div className="flex h-10 items-center gap-0.5 border-b border-solid border-sf-gray-100 px-2 py-1">
                    <Search
                        onKeyDown={(e) => {
                            const isEmptyList = !searchQuery.length;
                            // MUI Menu component for some reason take the focus once we type the first letter
                            // of the first item in the list. to prevent it, we stop the keyDown propagation
                            if (searchQuery[0]?.startsWith(e.key) || searchQuery[0]?.startsWith(e.key.toUpperCase())) {
                                e.stopPropagation();
                            }
                            // In order to handle the empty list message - No Results Found
                            if (isEmptyList && e.key.toLowerCase() === 'n') {
                                e.stopPropagation();
                            }
                        }}
                        autoFocus
                        width={'100%'}
                        onChange={onSearch}
                    />
                </div>
                <div className="w-full flex flex-1">
                    <List className="w-full flex-grow max-h-[120px] overflow-y-auto">
                        {!isLoading &&
                            data?.map((entity) => (
                                <ListItem
                                    key={entity.guid}
                                    value={entity}
                                    IconLeft={ENTITY_TYPE_TO_ICON[entity.type] ?? Icons.Add}
                                    title={entity[displayedField]}
                                    subtitle={entity.domainName}
                                    onCheck={entitySelected}
                                    onClick={entitySelected}
                                    multiSelect
                                    selected={selectedEntities.some(
                                        (selectedEntity) => selectedEntity.guid === entity.guid
                                    )}
                                />
                            ))}
                        {isLoading && <ListItem title={'Loading ...'} />}
                    </List>
                    <List className="w-full flex-grow max-h-[120px] overflow-y-auto">
                        {selectedEntities?.map((entity) => (
                            <ListItem
                                key={entity.guid}
                                value={entity}
                                IconLeft={ENTITY_TYPE_TO_ICON[entity.type] ?? Icons.Add}
                                title={entity[displayedField]}
                                subtitle={entity.domainName}
                                onRemove={entitySelected}
                            />
                        ))}
                    </List>
                </div>
            </div>
            <div className="px-4 border-t w-full h-14 flex justify-end items-center">
                <Button text="Done" onClick={onClose} buttonStyle={ButtonStyles.PrimarySolid} />
            </div>
        </div>
    );
};

export default EntitiesSearch;
